import { Button, Component, Node, _decorator } from 'cc';
import Lobby from '../../../System/Lobby';
import NumberFormatter from '../../../../common/scripts/Toolkit/Components/Label/NumberFormatter';
import MultiThemeSource from '../../../../common/scripts/Toolkit/Components/MultiTheme/MultiThemeSource';
import RollingNumber from '../../../../common/scripts/Toolkit/Components/Effect/RollingNumber/RollingNumber';
import Page from '../../../../common/scripts/Toolkit/Components/Page/Page';
import { Music } from '../../Data/Config/AudioConfig';
import { FeatureActivityEventName } from '../../Data/Config/FeatureActivityConfig';
import Delay from '../../../../common/scripts/Toolkit/Utils/Delay';
import SceneManager from '../../../../common/scripts/SceneManager';
import JackpotRecordPage from './JackpotRecordPage';
import JackpotModel, { JackpotLevel } from '../../../../tableEngine/Script/Game/Data/Model/LobbyJackpotModel';
import NodeName from '../../Data/Config/SceneNodeName';
import { PageName } from '../../../../lobbyCommunicator/Data/Lobby.dto';

const { ccclass, property, requireComponent } = _decorator;

@ccclass('JackpotPage')
@requireComponent(Page)
export default class JackpotPage extends Component {
    @property(Button)
    private readonly recordButton: Button = null;

    @property([MultiThemeSource])
    private readonly jackpotMultiThemeSources: MultiThemeSource[];

    @property(Node)
    private readonly loadingLabel: Node = null;

    private readonly _dropBeforeNumberMap: Map<MultiThemeSource, NumberFormatter> = new Map();
    private readonly _rollingNumberMap: Map<MultiThemeSource, RollingNumber> = new Map();

    private _page: Page = null;
    private _recordPage: Page = null;
    private _isContentInit: boolean = false;

    public get recordPage() {
        if (!this._recordPage) {
            this._recordPage = Lobby.scene.getNode(NodeName.JackpotRecordPage).getComponent(Page);
        }
        return this._recordPage;
    }

    private get page() {
        if (!this._page) {
            this._page = this.node.getComponent(Page);
        }
        return this._page;
    }

    protected onLoad() {
        this.page.on(Page.EVENT_TYPE.OPEN_START, this._onPageOpened, this);
        this.page.on(Page.EVENT_TYPE.CLOSE_END, this._onPageClosed, this);
        this.recordButton.node.on(Button.EventType.CLICK, () => {
            this.recordPage.open();
        });
        Delay.event(SceneManager.EVENT_TYPE.NODE_CREATED, Lobby.scene, (node: Node) => node.name === NodeName.JackpotRecordPage).then(() => {
            this.recordPage.on(Page.EVENT_TYPE.OPEN_START, () => {
                this.page.view.active = false;
            });
            this.recordPage.on(Page.EVENT_TYPE.CLOSE_END, () => {
                if (!this.recordPage.getComponent(JackpotRecordPage).isReturnToMenuPage) {
                    this.page.view.active = true;
                } else {
                    Lobby.scene.getNode(NodeName.LobbyMenuPage).getComponent(Page).open();
                }
            });
            if (this.recordPage.isVisible) {
                this.page.view.active = false;
            }
        });
        this.jackpotMultiThemeSources.forEach((source) => {
            const [dropBeforeNumber] = source.getComponentsInChildren(NumberFormatter);
            const rollingNumber = source.getComponentInChildren(RollingNumber);
            this._dropBeforeNumberMap.set(source, dropBeforeNumber);
            this._rollingNumberMap.set(source, rollingNumber);
        });
        this._updateContent();
    }

    private _onPageOpened() {
        Lobby.activity.send(FeatureActivityEventName.OpenJackpotPage, {
            From: Lobby.currentTable ? 'game' : 'lobby',
        });
        Lobby.audio.pauseMusic(0);
        Lobby.audio.playMusic(Music.BGM_JP, 1);
        Lobby.jackpot.on(JackpotModel.EVENT_TYPE.JACKPOT_STATUS_UPDATED, this._updateValue, this);
    }

    private _onPageClosed() {
        if (Lobby.sharedAsset.webViewManager.isGamePlaying) {
            Lobby.communicator.notifyGamePageClosed(PageName.Jackpot);
        }
        Lobby.audio.pauseMusic(1);
        if (!Lobby.sharedAsset.webViewManager.isGamePlaying) {
            Lobby.audio.resumeMusic(0);
        }
        Lobby.jackpot.off(JackpotModel.EVENT_TYPE.JACKPOT_STATUS_UPDATED, this._updateValue, this);
        Object.values(this._rollingNumberMap).forEach((rollingNumber) => rollingNumber.stop(true));
        this._updateValue(true);
    }

    private async _updateContent() {
        // HACK
        // this.jackpotMultiThemeSources.forEach((source, idx) => {
        //     source.node.active = idx < poolList.length;
        // });
        this.jackpotMultiThemeSources.forEach((source) => {
            source.node.active = false;
        });

        const poolList = await this._getPoolList();
        poolList?.forEach((pool) => {
            const themeIndex = JackpotLevel.list().indexOf(pool.Level);
            const multiThemeSource = this.jackpotMultiThemeSources[themeIndex];
            multiThemeSource.theme = themeIndex;
            multiThemeSource.node.active = true;
            const dropBeforeNumber = this._dropBeforeNumberMap.get(multiThemeSource);
            dropBeforeNumber.value = Lobby.account.valueToDollar(pool.TriggerUpperLimit);
        });
        this._isContentInit = true;
        this._updateValue(true);
    }

    private async _updateValue(setDirectly: boolean = false) {
        if (!this._isContentInit) {
            return;
        }
        const poolList = await this._getPoolList();
        poolList?.forEach(({ Level, Value }) => {
            const displayValue = Lobby.user.transformToCurrency(Value);
            const rollingNumber = this._rollingNumberMap.get(this.jackpotMultiThemeSources[JackpotLevel.list().indexOf(Level)]);
            if (setDirectly) {
                rollingNumber.currentValue = displayValue;
            } else {
                rollingNumber.startRolling(displayValue);
            }
        });
    }

    private async _getPoolList() {
        if (!Lobby.jackpot.poolList[Lobby.hall.currentHallID]) {
            this.loadingLabel.active = true;
            Lobby.jackpot.requestJackpotStatus();
            await Delay.event(JackpotModel.EVENT_TYPE.JACKPOT_STATUS_UPDATED, Lobby.jackpot);
            this.loadingLabel.active = false;
        }
        return Lobby.jackpot.poolList[Lobby.hall.currentHallID];
    }
}
